interface CurrencyOptions {
  symbol?: string;
  code?: string;
}

/** Formats a millisecond duration as "45 min", "1h" or "1h 30m". */
export const formatDuration = (
  milliseconds?: number | null,
): string | undefined => {
  if (milliseconds == null || milliseconds <= 0) return undefined;
  const minutes = Math.round(milliseconds / 60000);
  if (minutes <= 0) return undefined;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest > 0 ? `${hours}h ${rest}m` : `${hours}h`;
  }
  return `${minutes} min`;
};

/** "SMASHD'd it (DHA)" -> trimmed, collapsed whitespace. */
export const formattedStoreName = (name = ""): string =>
  name.replace(/\s+/g, " ").trim();

/** Formats a time-of-day expressed in ms since midnight, e.g. 32400000 -> "09:00 AM". */
export const formatTimeOfDay = (milliseconds?: number | null): string => {
  if (milliseconds == null) return "";
  const totalMinutes = Math.floor(milliseconds / 60000);
  const hours24 = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  const suffix = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${String(hours12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${suffix}`;
};

const CALENDAR_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/** moment's `LT`: "2:30 PM" — no leading zero on the hour, unlike `formatTimeOfDay`. */
const clockTime = (date: Date): string => {
  const hours24 = date.getHours();
  const suffix = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${String(date.getMinutes()).padStart(2, "0")} ${suffix}`;
};

/**
 * `moment(date).calendar()` with moment's default English formats, so the
 * reviews list reads "Today at 2:30 PM" / "Last Friday at 8:15 PM" and falls
 * back to `MM/DD/YYYY` beyond a week either way.
 *
 * Reproduced rather than pulled in: `moment` is a dependency of the original
 * and not of this tree, and this is the only place its output is user-visible.
 * The thresholds are moment's own — same day, ±1 day, then the surrounding
 * week measured in whole calendar days, not in hours.
 */
export const calendarDate = (
  value?: string | number | Date | null,
  now: Date = new Date(),
): string => {
  if (value == null) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const startOf = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(date) - startOf(now)) / 86400000);
  const at = clockTime(date);

  if (days === 0) return `Today at ${at}`;
  if (days === 1) return `Tomorrow at ${at}`;
  if (days === -1) return `Yesterday at ${at}`;
  if (days > 1 && days < 7) return `${CALENDAR_DAYS[date.getDay()]} at ${at}`;
  if (days < -1 && days > -7) {
    return `Last ${CALENDAR_DAYS[date.getDay()]} at ${at}`;
  }

  // moment's `L` for English: MM/DD/YYYY.
  return [
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
    date.getFullYear(),
  ].join("/");
};

/**
 * `parseNum` — module `7423`, verbatim. Rounds to two decimals by way of an
 * integer parse, so it TRUNCATES the third decimal rather than rounding it:
 * `parseNum(99.456)` is `99.45`, not `99.46`. The second parameter is the
 * original's own "keep the sign off" flag.
 *
 * ```js
 * function T(e, t) {
 *   let r = parseInt((100 * Math.abs(e)).toFixed(2), 10) / 100;
 *   return e < 0 && !t ? -r : r;
 * }
 * ```
 */
export const parseNum = (value: number, keepAbsolute = false): number => {
  const rounded = parseInt((100 * Math.abs(value)).toFixed(2), 10) / 100;
  return value < 0 && !keepAbsolute ? -rounded : rounded;
};

/**
 * `sentenceCase` (`S`, module `7423`) — `"POS TAKE-AWAY"` → `"Pos Take-away"`.
 *
 * ```js
 * function v(e) { return e.charAt(0).toUpperCase() + e.slice(1).toLowerCase(); }
 * function S(e) { return e.split(" ").map((e) => v(e)).join(" "); }
 * ```
 *
 * It is NOT `constants/enums.ts`'s `titleCase`, and the difference is visible:
 * that one splits on hyphens and underscores as well as on spaces, so it
 * renders `"Pos Take Away"` where this renders `"Pos Take-away"`. The X Report
 * labels its platform and order-status sub-channels with THIS one, so a
 * hyphenated platform keeps both its hyphen and its lower-case second half.
 */
export const sentenceCase = (value = ""): string =>
  value
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

/** `R` — the thousands separator, applied to the integer part only. */
const withThousands = (digits: string): string =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/**
 * `formatMoneyAmount` (`x`) — the unprefixed money string.
 *
 * The subtle step is the last one: the amount is fixed to two decimals and
 * then **trailing zeros are stripped**, so `1200 -> "1,200"`, `1200.5 ->
 * "1,200.5"`, `1200.05 -> "1,200.05"`. The original never emits `".00"`.
 *
 * There is no `Intl.NumberFormat` here despite the `localeAmount` name, and
 * that is deliberate to reproduce: `toLocaleString` follows the VISITOR's
 * locale and would render `1.200,5` in a German browser.
 */
export const formatMoneyAmount = (value: number): string => {
  const rounded = parseNum(value);
  const [whole, fraction = ""] = Math.abs(rounded).toFixed(2).split(".");
  const trimmed = fraction.replace(/0+$/, "");
  const sign = rounded < 0 ? "-" : "";
  return `${sign}${withThousands(whole)}${trimmed ? `.${trimmed}` : ""}`;
};

/**
 * `localeAmount(currencySymbol, amount)` (`D`) — the ONLY money formatter in
 * the original. The symbol is concatenated with **no separator**, so a tenant
 * whose `currency_symbol` is `"Rs."` reads `"Rs.1,200"` and one whose symbol
 * carries its own trailing space reads `"Rs 1,200"`.
 */
export const localeAmount = (
  symbol: string | null | undefined,
  amount: number,
): string => `${symbol ?? ""}${formatMoneyAmount(amount)}`;

/**
 * Kept for the surfaces that already call it; it now delegates to
 * `formatMoneyAmount` so every money string in the app shares the original's
 * rounding and grouping rather than the visitor's locale. The `code` fallback
 * has no counterpart in the original and only fires for a tenant with no
 * `currency_symbol` at all.
 */
export const formatCurrency = (
  amount?: number | null,
  { symbol = "", code = "" }: CurrencyOptions = {},
): string => {
  const formatted = formatMoneyAmount(Number(amount ?? 0));
  return `${symbol || (code ? `${code} ` : "")}${formatted}`.trim();
};
